import { useEffect, useState, type FormEvent } from 'react';
import { Link, navigate } from '../lib/router';
import { landingPage, login, passwordChangePath, useMe, type CodeDelivery, type LoginResult } from '../stores/session';
import { AuthLayout } from '../components/AuthLayout';
import { EMAIL_RE, errorMessage, Field, FormError, Heading, PasswordField, PrimaryButton } from '../components/AuthForm';
import { OtpStep, useNext } from '../components/OtpStep';
import { StatusBadge } from '../components/StatusBadge';
import { TelegramStep } from '../components/TelegramStep';
import { api, ApiError } from '../lib/api';

type Challenge =
  | { challengeId: string; method: 'email' | 'totp'; devCode?: string; telegramAvailable?: boolean; resendIn?: number }
  | { challengeId: string; method: 'telegram'; telegram: CodeDelivery; authenticatorFallback: boolean };

const challengeOf = (res: Extract<LoginResult, { otpRequired: true }>): Challenge =>
  res.method === 'telegram'
    ? { challengeId: res.challengeId, method: 'telegram', telegram: res.telegram, authenticatorFallback: res.authenticatorFallback }
    : { challengeId: res.challengeId, method: res.method ?? 'email', devCode: res.devCode, telegramAvailable: res.telegramAvailable, resendIn: res.resendIn };

export function LoginPage() {
  const me = useMe();
  const next = useNext();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Already logged in (or just finished logging in): leave the page.
  useEffect(() => {
    if (me) navigate(landingPage(next), { replace: true });
  }, [me, next]);

  return (
    <AuthLayout title="Log in">
      {!challenge ? (
        <CredentialsStep
          email={email}
          password={password}
          onEmail={setEmail}
          onPassword={setPassword}
          onChallenge={setChallenge}
        />
      ) : challenge.method === 'telegram' ? (
        <TelegramStep
          challengeId={challenge.challengeId}
          telegram={challenge.telegram}
          authenticatorFallback={challenge.authenticatorFallback}
          onAuthenticator={() => setChallenge({ challengeId: challenge.challengeId, method: 'totp' })}
          onBack={() => {
            setPassword('');
            setChallenge(null);
          }}
        />
      ) : (
        <OtpStep
          email={email}
          challengeId={challenge.challengeId}
          method={challenge.method}
          devCode={challenge.devCode}
          resendIn={challenge.resendIn}
          onUseTelegram={
            challenge.telegramAvailable
              ? async () => {
                  const r = await api.post<{ challengeId: string; telegram: CodeDelivery; authenticatorFallback: boolean }>('/api/auth/use-telegram', {
                    challengeId: challenge.challengeId,
                  });
                  setChallenge({ challengeId: r.challengeId, method: 'telegram', telegram: r.telegram, authenticatorFallback: r.authenticatorFallback });
                }
              : undefined
          }
          onResend={async () => {
            // Logging in again emails a new code (the API keeps the last one if it's under a minute old).
            const res = await login(email.trim(), password);
            if (!res.otpRequired) return;
            const c = challengeOf(res);
            setChallenge(c);
            return 'resendIn' in c ? c.resendIn : undefined;
          }}
          onBack={() => {
            setPassword('');
            setChallenge(null);
          }}
        />
      )}
    </AuthLayout>
  );
}

function CredentialsStep(props: {
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onChallenge: (c: Challenge) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notSetUp, setNotSetUp] = useState<string | null>(null);
  const canSubmit = EMAIL_RE.test(props.email.trim()) && props.password.length > 0 && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // Without OTP (dev) this logs straight in and the page redirects via `me`.
      const res = await login(props.email.trim(), props.password);
      if (res.otpRequired) props.onChallenge(challengeOf(res));
      else {
        const changePassword = passwordChangePath(res);
        if (changePassword) navigate(changePassword, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'tg2fa_not_set_up') setNotSetUp(err.message);
      else setError(errorMessage(err));
      setBusy(false);
    }
  }

  // Staff whose Telegram sign-in isn't set up (STAFF_TG2FA=required): after a correct password.
  if (notSetUp) {
    return (
      <>
        <StatusBadge kind="alert" />
        <Heading title="Telegram isn’t set up" subtitle="Your sign-in needs the Telegram you link in the CRM" />
        <p className="mt-3.5 text-[15px] leading-relaxed text-muted">{notSetUp}</p>
        <button
          type="button"
          className="mt-8 w-full rounded-2xl border border-line-strong py-3 text-[15px] font-semibold text-fg"
          onClick={() => {
            props.onPassword('');
            setNotSetUp(null);
          }}
        >
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <>
      <Heading title="Welcome back" subtitle="Ready to trade?" />
      <p className="mt-3.5 text-[15px] text-muted">
        New here?{' '}
        <Link to="/register" className="font-semibold text-accent-fg">
          Create an account →
        </Link>
      </p>

      <form onSubmit={submit} noValidate className="mt-8">
        <Field
          id="email"
          label="Email"
          type="email"
          autoComplete="email"
          value={props.email}
          onChange={props.onEmail}
          autoFocus
        />
        <PasswordField
          id="password"
          label="Password"
          autoComplete="current-password"
          value={props.password}
          onChange={props.onPassword}
        />

        <div className="mx-1 mb-[22px] mt-1.5 flex justify-end text-sm">
          <Link
            to={`/forgot-password${EMAIL_RE.test(props.email.trim()) ? `?email=${encodeURIComponent(props.email.trim())}` : ''}`}
            className="font-semibold text-accent-fg"
          >
            Forgot password?
          </Link>
        </div>

        <FormError message={error} />
        <PrimaryButton disabled={!canSubmit} busy={busy} busyLabel="Logging in…">
          Log in
        </PrimaryButton>
      </form>
    </>
  );
}
